import React from "react";
import { DiscussionWithoutContentDTO, GroupDTO, SeriesDTO, UserProfileDTO } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { HomeDiscoveryBootstrapResponse, HomeViewerGraphTier } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { onGroupMemberAdded, onUserFollowAdded } from "@/main/common/EventRegistry";
import { Localizer } from "@/main/react/Types";
import { createReactMount } from "@/main/react/mountContract";
import logger from "@/lib/logger";
import { HomeDiscoveryRail } from "./home-page/HomeDiscoveryRail";
import { HomeFeedList } from "./home-page/HomeFeedList";
import {
    HomeFeedSelection,
    HomeFeedType,
    HomeOrderingStyle,
    HomeRange,
    LoadableItems,
    feedStateToSelection,
    homeFeedTypeSupportsSort,
    homeOrderingSupportsRange,
    isTransientHomeFeedType,
    normalizeScopedHomeOrderingStyle,
    selectionToFeedState,
} from "./home-page/HomePageShared";
import { resolveSmallestRangeWithContent } from "./home-page/homeRangeAutoNarrow";
import { HomePageHeader } from "@/main/react/page/home-page/HomePageHeader";
import { useFloatingActionGroup } from "@/main/react/floating-actions/FloatingActionContributionStore";
import { CREATE_DISCUSSION_ACTION } from "@/main/react/floating-actions/FloatingActionConfig";
import { prefersReducedMotion } from "@/main/util/motionPreference";
import { ArrowUp } from "lucide-react";
import { ReadingLanguageAddedStrip } from "@/main/react/page/home-page/ReadingLanguageAddedStrip";
import { AndroidAppPromoStrip } from "@/main/react/page/home-page/AndroidAppPromoStrip";
import { DistinctionCelebrationSlot } from "@/main/react/distinction/DistinctionCelebrationSlot";
import { PageWithRailLayout } from "@/main/react/page/PageWithRailLayout";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";

type HomePageState = {
    feedType: HomeFeedType;
    orderingStyle: HomeOrderingStyle;
    range: HomeRange;
};

type RememberedAllState = {
    orderingStyle: HomeOrderingStyle;
    topRange: HomeRange;
};

type HomeFeedPreferencePayload = {
    defaultFeedType: "HOME" | "ALL" | "GROUPS" | "FOLLOWING" | "SERIES";
    allOrderingStyle: "AGREED" | "CONTROVERSIAL" | "ARGUMENTS" | "ACTIVE" | "NEW";
    allTopRange: "ALL" | "DAY" | "WEEK" | "MONTH" | "YEAR";
};

const HOME_PAGE_CANONICAL_PATH = "/";
const HOME_FEED_PREFERENCE_SAVE_DEBOUNCE_MS = 400;

function getDefaultFeedType(isLoggedIn: boolean): HomeFeedType {
    return isLoggedIn ? "home" : "all";
}

function normalizeFeedType(value: string | null | undefined, isLoggedIn: boolean): HomeFeedType {
    if (value === "all") {
        return "all";
    }

    if (value === "home") {
        return isLoggedIn ? "home" : "all";
    }

    // "Your groups" is a members-only view; anonymous visitors fall back to the default feed.
    if (value === "groups") {
        return isLoggedIn ? "groups" : "all";
    }

    // The "Boards to vote on" lane is the OPPOSITE case to the one below, and permanently so. Its row
    // set is "boards you have not voted on", which without a viewer is not a narrower list but a
    // meaningless one — so /ajax/page/discussions/boards/to-vote-on is deliberately absent from
    // WebSecurityConfig's anonymous matchers and answers 401 (AjaxPaginationController says so in its
    // own javadoc). The feed option carries `personal: true`, so a logged-out viewer cannot PICK the
    // lane; this line is the other path, the one that does not go through the picker at all. Without
    // it a shared link would put an anonymous reader on a lane that can only fail.
    if (value === "boards") {
        return isLoggedIn ? "boards" : "all";
    }

    // The two audience scopes are members-only for the same reason as groups: the corpus is DEFINED
    // by the viewer's follow graph, and their endpoints are absent from the anonymous allowlist. The
    // server normalizes an anonymous ?feed=following / ?feed=series to `all` the same way
    // (HomeController.normalizeFeedType), so a shared link degrades rather than 401s.
    if (value === "following") {
        return isLoggedIn ? "following" : "all";
    }

    if (value === "series") {
        return isLoggedIn ? "series" : "all";
    }

    // The "No replies yet" lane is open to everyone as of 2026-09-01. It used to collapse to `all` for
    // anonymous viewers because /ajax/page/discussions/questions/unanswered was missing from
    // WebSecurityConfig's allowlist and 401'd — an AUTH fact, never a scope fact, and the reason a
    // shared ?feed=unanswered link silently redirected. The endpoint is allowlisted now, so the
    // collapse would only hide a lane that answers. Nothing about the lane narrows its audience: the
    // row set does not depend on the viewer.
    //
    // This branch was DELETED rather than widened in c1146659e6 (2026-09-01), so for twelve days the
    // lane normalized to the default feed for everyone: a server render of
    // data-feed-type="unanswered" was re-normalized away at mount, and picking "No replies yet" in the
    // selector was a no-op because handleSelectionChange passes the pick through this function before
    // storing it. Restored 2026-09-13 (audience-scopes design §7.4, A11), red-first in HomePage.spec.
    if (value === "unanswered") {
        return "unanswered";
    }

    return getDefaultFeedType(isLoggedIn);
}

function normalizeOrderingStyle(value?: string | null): HomeOrderingStyle {
    switch (value) {
        case "agreed":
        // `HOME-FEED-6` (wave 8, batch J) — `top` is the RETIRED name of this ordering, not an unknown
        // value. It was a real emitted URL param until 23cda9dfe (2026-06-11) replaced the Top sort with
        // "Most agreed" and "Most controversial", and that migration renamed the persisted `TOP` rows to
        // `AGREED`: the preference was carried across and the URL was not. Collapsing it onto `active`
        // instead meant an old bookmark or shared link opened the all-site Active feed while the address
        // bar still read `?ordering=top&range=week` — and because `active` supports no range,
        // `homeOrderingSupportsRange` silently dropped `range=week` as well. The URL is only rebuilt when
        // the member CHANGES something, so nothing ever corrected it and the link they copied onward
        // carried the same lie forward. Mapping it to its documented successor makes the two halves agree
        // and keeps the range, which is what a top-of-week link was asking for.
        case "top":
            return "agreed";
        case "new":
            return "new";
        case "controversial":
            return "controversial";
        case "arguments":
            return "arguments";
        // "active" is the default; anything unknown collapses onto it
        default:
            return "active";
    }
}

function normalizeRange(value?: string | null): HomeRange {
    switch (value) {
        case "day":
        case "week":
        case "month":
        case "year":
            return value;
        default:
            return "all";
    }
}

function normalizeHomePageState(state: HomePageState, isLoggedIn: boolean): HomePageState {
    const feedType = normalizeFeedType(state.feedType, isLoggedIn);

    // Home is the one single-ordering view among the personal feeds: its blend is ranked, so the
    // ordering is an unused placeholder (kept as "agreed" for parity with the server's resolver).
    if (feedType === "home") {
        return {
            feedType,
            orderingStyle: "agreed",
            range: "all",
        };
    }

    // "In my groups" and the two audience scopes are SORTABLE (audience-scopes design §4, HS-3a): they
    // keep the sort they are handed, normalized to the three a scope serves, and the window applies
    // exactly when the sort takes one. The same rule `HomeFeedPreferenceResolver` applies server-side.
    if (feedType === "groups" || feedType === "following" || feedType === "series") {
        const scopedOrderingStyle = normalizeScopedHomeOrderingStyle(normalizeOrderingStyle(state.orderingStyle));
        return {
            feedType,
            orderingStyle: scopedOrderingStyle,
            range: homeOrderingSupportsRange(scopedOrderingStyle) ? normalizeRange(state.range) : "all",
        };
    }

    // "No replies yet" is single-ordering too, but on a recency cursor rather than a count sort. The
    // board lane is the same shape: the server owns its order (A2), so there is no ordering or range
    // axis to offer and the placeholder must not leak into the URL.
    if (feedType === "unanswered" || feedType === "boards") {
        return {
            feedType,
            orderingStyle: "new",
            range: "all",
        };
    }

    const orderingStyle = normalizeOrderingStyle(state.orderingStyle);

    return {
        feedType,
        orderingStyle,
        range: homeOrderingSupportsRange(orderingStyle) ? normalizeRange(state.range) : "all",
    };
}

function normalizeRememberedAllState(orderingStyle?: string | null, topRange?: string | null): RememberedAllState {
    return {
        orderingStyle: normalizeOrderingStyle(orderingStyle),
        topRange: normalizeRange(topRange),
    };
}

function parseHomePageState(search: string, fallback: HomePageState, isLoggedIn: boolean): HomePageState {
    const params = new URLSearchParams(search);

    // The server's rule, restated (`HomeFeedPreferenceResolver.resolvePageState`): an address that
    // names ANY of the three parameters is an explicit request, and what it omits means the default
    // (`active`, no window), not the stored preference. `fallback` is the server-rendered initial state
    // and is the answer only for a bare address. This matters on Back: `buildHomePageUrl` omits
    // `ordering` for `active`, and `useHistoryBackedState` re-parses every popstate against the INITIAL
    // state, so a member who started on Home (placeholder `agreed`), sorted by Active, then by New, and
    // pressed Back would otherwise land on Most upvoted with the address bar reading Active.
    const hasExplicitState = params.has("feed") || params.has("ordering") || params.has("range");

    return normalizeHomePageState({
        feedType: normalizeFeedType(params.get("feed") ?? fallback.feedType, isLoggedIn),
        orderingStyle: normalizeOrderingStyle(params.get("ordering") ?? (hasExplicitState ? "active" : fallback.orderingStyle)),
        range: normalizeRange(params.get("range") ?? (hasExplicitState ? "all" : fallback.range)),
    }, isLoggedIn);
}

function areHomePageStatesEqual(left: HomePageState, right: HomePageState): boolean {
    return left.feedType === right.feedType
        && left.orderingStyle === right.orderingStyle
        && left.range === right.range;
}

export function buildHomePageUrl(
    state: HomePageState,
    location: { pathname: string; search: string },
    isLoggedIn: boolean,
): string {
    const normalizedState = normalizeHomePageState(state, isLoggedIn);
    const params = new URLSearchParams(location.search);
    const defaultFeedType = getDefaultFeedType(isLoggedIn);

    if (normalizedState.feedType === defaultFeedType) {
        params.delete("feed");
    } else {
        params.set("feed", normalizedState.feedType);
    }

    // Every feed with a sort axis carries an ordering param, and only when it isn't the default
    // ("active", which is also what the server resolves an explicit `?feed=` with no `?ordering=` to).
    // "home" is single-ordering (its orderingStyle is an unused placeholder), as are the two lanes.
    if (!homeFeedTypeSupportsSort(normalizedState.feedType) || normalizedState.orderingStyle === "active") {
        params.delete("ordering");
    } else {
        params.set("ordering", normalizedState.orderingStyle);
    }

    if (normalizedState.feedType !== "home" && homeOrderingSupportsRange(normalizedState.orderingStyle) && normalizedState.range !== "all") {
        params.set("range", normalizedState.range);
    } else {
        params.delete("range");
    }

    const query = params.toString();
    return `${HOME_PAGE_CANONICAL_PATH}${query ? `?${query}` : ""}`;
}

function toHomeFeedDefaultType(feedType: HomeFeedType): HomeFeedPreferencePayload["defaultFeedType"] {
    if (feedType === "all") {
        return "ALL";
    }

    if (feedType === "groups") {
        return "GROUPS";
    }

    // Both persist as sticky defaults like GROUPS: `HomeFeedDefaultType` and the CHECK on
    // application_users accept them (audience-scopes design §8). Falling through to HOME here would
    // silently rewrite the member's real default, which is the defect isTransientHomeFeedType names.
    if (feedType === "following") {
        return "FOLLOWING";
    }

    if (feedType === "series") {
        return "SERIES";
    }

    return "HOME";
}

function toHomeFeedPreferencePayload(state: HomePageState, rememberedAllState: RememberedAllState): HomeFeedPreferencePayload {
    return {
        // Every selection is a sticky default now — including "groups". The remembered all-feed
        // ordering/range is preserved independently so switching to a count sort restores the last range.
        defaultFeedType: toHomeFeedDefaultType(state.feedType),
        allOrderingStyle: rememberedAllState.orderingStyle.toUpperCase() as HomeFeedPreferencePayload["allOrderingStyle"],
        allTopRange: rememberedAllState.topRange.toUpperCase() as HomeFeedPreferencePayload["allTopRange"],
    };
}

function getEffectiveAllRange(rememberedAllState: RememberedAllState): HomeRange {
    return homeOrderingSupportsRange(rememberedAllState.orderingStyle) ? rememberedAllState.topRange : "all";
}

export function HomePage(props: {
    localizer: Localizer;
    initialFeedType?: HomeFeedType;
    initialOrderingStyle?: HomeOrderingStyle;
    initialRange?: HomeRange;
    initialAllOrderingStyle?: HomeOrderingStyle;
    initialAllRange?: HomeRange;
    // CWV L1: server-inlined first feed page for the anonymous default cold-load. When present it seeds
    // the feed so the mount-time fetch is skipped; honored only while the live feed still matches the
    // initial feed (see initialFeedItemsForCurrentFeed below).
    initialFeedItems?: DiscussionWithoutContentDTO[];
}) {
    const {
        localizer,
        initialFeedType = "home",
        initialOrderingStyle = "active",
        initialRange = "all",
        initialAllOrderingStyle = "active",
        initialAllRange = "all",
        initialFeedItems,
    } = props;
    const isLoggedIn = React.useMemo(() => MetaHandler.isUserLoggedIn(), []);
    const defaultState = React.useMemo<HomePageState>(() => ({
        feedType: normalizeFeedType(initialFeedType, isLoggedIn),
        orderingStyle: normalizeOrderingStyle(initialOrderingStyle),
        range: normalizeRange(initialRange),
    }), [initialFeedType, initialOrderingStyle, initialRange, isLoggedIn]);
    const initialPageState = React.useMemo<HomePageState>(
        () => normalizeHomePageState(defaultState, isLoggedIn),
        [defaultState, isLoggedIn],
    );
    const initialRememberedAllState = React.useMemo<RememberedAllState>(
        () => normalizeRememberedAllState(initialAllOrderingStyle, initialAllRange),
        [initialAllOrderingStyle, initialAllRange],
    );
    const { state: pageState, setState: setPageState } = useHistoryBackedState<HomePageState>({
        initialState: initialPageState,
        readState: (location, fallback) => parseHomePageState(location.search, fallback, isLoggedIn),
        buildUrl: (state, location) => buildHomePageUrl(state, location, isLoggedIn),
        areEqual: areHomePageStatesEqual,
    });
    const [rememberedAllState, setRememberedAllState] = React.useState<RememberedAllState>(initialRememberedAllState);
    const [historyState, setHistoryState] = React.useState<LoadableItems<DiscussionWithoutContentDTO>>({
        items: [],
        loading: isLoggedIn,
        error: null,
    });
    const [seriesState, setSeriesState] = React.useState<LoadableItems<SeriesDTO>>({ items: [], loading: true, error: null });
    const [groupsState, setGroupsState] = React.useState<LoadableItems<GroupDTO>>({ items: [], loading: true, error: null });
    const [usersState, setUsersState] = React.useState<LoadableItems<UserProfileDTO>>({ items: [], loading: true, error: null });
    /**
     * `HOME-FEED-27` (operator decision **D25**) — the member's OWN groups.
     *
     * <p>Loaded here rather than inside either renderer, for one mechanical reason: the desktop rail
     * (`hidden lg:block`) and the inline rows (`lg:hidden`) both MOUNT at every width, so a fetch owned
     * by either one would be issued twice on every groups-feed load. One owner, one request, both
     * consumers.
     *
     * <p>`loading: false` initially, and the effect below is the only thing that ever sets it true.
     * The default `home` feed never triggers it, so this adds nothing to the startup path — the
     * constraint `HOME-FEED-9` records and the reason D25 could be built without a perf question.
     */
    const [yourGroupsState, setYourGroupsState] = React.useState<LoadableItems<GroupDTO>>({ items: [], loading: false, error: null });
    /**
     * `HOME-FEED-9` / `HOME-FEED-10` — the viewer's own personalisation graph, as the tier the ranking
     * SQL already computes. Rides the discovery bootstrap this page already fetches once per mount,
     * so it costs no request (batch 3's design doc, §3).
     *
     * <p>Held as a bare string union rather than the whole signal object, deliberately. Rule 31's
     * concern is a value whose IDENTITY changes on every write even when its content does not: a
     * re-poll of the bootstrap for an unchanged graph rebuilds the object from the wire and would
     * therefore re-render both renderers for nothing, while `setState` on an identical primitive is a
     * bail-out React performs for us. Nothing on this screen reads the three counts; if something ever
     * does, it must compare by CONTENT before storing, not by reference.
     *
     * <p>`null` means anonymous, a server without the field, or a bootstrap in flight - all three read
     * as "no adaptation", which is the behaviour that shipped before the signal existed.
     */
    const [viewerGraphTier, setViewerGraphTier] = React.useState<HomeViewerGraphTier | null>(null);
    const yourGroupsRequestIdRef = React.useRef(0);
    const yourGroupsLoadedRef = React.useRef(false);
    const discoveryRequestIdRef = React.useRef(0);
    const hasUserChangedPreferencesRef = React.useRef(false);
    /**
     * Range auto-narrow (2026-09-01). Both of these guard the same behaviour: on a switch to a
     * count-based sort, resolve the time window to the smallest one that still holds a full page,
     * instead of restoring an all-time default that makes every "best of" sort a frozen leaderboard.
     * See designs/01-now/2026-09-01-home-feed-range-auto-narrow-on-sort-change-design.md.
     *
     * <p>`rangePinnedByMemberRef` — the moment the member uses the Range pill themselves, resolution
     * stops for the rest of the page's life. Their explicit choice is never silently overridden on the
     * next sort switch. Deliberately a ref rather than persisted state: a member returning tomorrow
     * gets freshness again, which is the point of the feature.
     *
     * <p>`rangeResolveRequestIdRef` — monotonic, so a slow probe for a selection the member has since
     * moved off cannot commit over the newer one.
     */
    const rangePinnedByMemberRef = React.useRef(false);
    const rangeResolveRequestIdRef = React.useRef(0);
    /**
     * The sort the member has just picked, while its window is still being resolved.
     *
     * <p>Resolution runs before the state commit (one push, no replace exemption), which without this
     * would leave the pill snapped back to the previous sort for the length of a probe — the control
     * would read as broken. `pendingSelection` is presentation only: it moves the pill immediately and
     * nothing else, so the feed below keeps rendering the posts the member is still looking at until
     * the real commit lands. `null` at every other moment.
     */
    const [pendingSelection, setPendingSelection] = React.useState<HomeFeedSelection | null>(null);
    const saveInFlightRef = React.useRef(false);
    const queuedPreferencePayloadRef = React.useRef<HomeFeedPreferencePayload | null>(null);
    const lastSavedPreferenceSignatureRef = React.useRef<string>(JSON.stringify(toHomeFeedPreferencePayload(initialPageState, initialRememberedAllState)));

    const { feedType, orderingStyle, range } = pageState;
    // CWV L1: the inlined seed belongs to the initial feed key only. Honor it while the live feed still
    // matches that key; once the user switches feeds, drop the seed so navigating back refetches fresh
    // data instead of re-seeding a now-stale first page.
    const initialFeedKey = `${initialPageState.feedType}-${initialPageState.orderingStyle}-${initialPageState.range}`;
    const currentFeedKey = `${feedType}-${orderingStyle}-${range}`;
    const initialFeedItemsRef = React.useRef(initialFeedItems);
    React.useEffect(() => {
        if (currentFeedKey !== initialFeedKey) {
            initialFeedItemsRef.current = undefined;
        }
    }, [currentFeedKey, initialFeedKey]);
    const initialFeedItemsForCurrentFeed = currentFeedKey === initialFeedKey ? initialFeedItemsRef.current : undefined;
    React.useEffect(() => {
        setRememberedAllState(initialRememberedAllState);
        hasUserChangedPreferencesRef.current = false;
        saveInFlightRef.current = false;
        queuedPreferencePayloadRef.current = null;
        lastSavedPreferenceSignatureRef.current = JSON.stringify(toHomeFeedPreferencePayload(initialPageState, initialRememberedAllState));
    }, [initialPageState, initialRememberedAllState]);

    const flushPreferenceSaveQueue = React.useCallback(async () => {
        if (!isLoggedIn || saveInFlightRef.current || !queuedPreferencePayloadRef.current) {
            return;
        }

        saveInFlightRef.current = true;

        while (queuedPreferencePayloadRef.current) {
            const payload = queuedPreferencePayloadRef.current;
            queuedPreferencePayloadRef.current = null;
            const signature = JSON.stringify(payload);

            try {
                await ClientFactory.updateHomeFeedPreference(payload);
                lastSavedPreferenceSignatureRef.current = signature;
            } catch (error) {
                logger.error("[HomePage] failed to persist home feed preference", error);
            }
        }

        saveInFlightRef.current = false;
    }, [isLoggedIn]);

    React.useEffect(() => {
        if (!isLoggedIn || !hasUserChangedPreferencesRef.current) {
            return;
        }

        // A transient lane is a detour, not a default. Persisting here would not fail — the payload
        // mapper falls through to "HOME" — it would quietly replace the member's chosen default, and
        // they would only discover it on their next visit. Leave the stored preference alone.
        if (isTransientHomeFeedType(pageState.feedType)) {
            return;
        }

        const payload = toHomeFeedPreferencePayload(pageState, rememberedAllState);
        const signature = JSON.stringify(payload);

        if (signature === lastSavedPreferenceSignatureRef.current && !saveInFlightRef.current) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            queuedPreferencePayloadRef.current = payload;
            void flushPreferenceSaveQueue();
        }, HOME_FEED_PREFERENCE_SAVE_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [flushPreferenceSaveQueue, isLoggedIn, pageState, rememberedAllState]);

    const selection = React.useMemo<HomeFeedSelection>(
        () => feedStateToSelection(feedType, orderingStyle),
        [feedType, orderingStyle],
    );
    // What the pill shows: the optimistic pick while a range probe is in flight, the committed feed
    // state at every other moment.
    const displayedSelection = pendingSelection ?? selection;
    // Same rule for the sort pill: a fused-row pick in flight is a sort pick in flight.
    const displayedOrderingStyle = pendingSelection === null
        ? orderingStyle
        : selectionToFeedState(pendingSelection, orderingStyle).orderingStyle;
    /**
     * Cancel an in-flight range probe when the page state moves underneath it.
     *
     * <p>The only writer of `pageState` during a probe is something OTHER than this component — a
     * `popstate` from the member pressing Back, which `useHistoryBackedState` applies directly. Our own
     * commit clears `pendingSelection` in the same batch as it writes `pageState`, so this effect sees
     * `null` and does nothing on the normal path. Without it, a probe that resolves after a Back would
     * push a fresh entry for a sort the member has already left — the exact class of defect rule 33
     * exists to prevent, and one that would present as "Back didn't go back".
     */
    React.useEffect(() => {
        if (pendingSelection === null) {
            return;
        }
        rangeResolveRequestIdRef.current += 1;
        setPendingSelection(null);
        // Deliberately keyed on `pageState` alone: re-running when `pendingSelection` itself changes
        // would cancel the probe on the very render that started it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageState]);
    // The single home feed selector folds the former feed-type + ordering axes together. Choosing an
    // "all" sort remembers it (and restores the remembered range for count sorts); choosing "home" or
    // "groups" is a single-ordering view.
    const handleSelectionChange = React.useCallback((value: HomeFeedSelection) => {
        hasUserChangedPreferencesRef.current = true;
        const { feedType: nextFeedType, orderingStyle: nextOrderingStyle } = selectionToFeedState(value);
        // EVERY option in this dropdown pushes (§13.1). This control is the reason the old
        // destination-versus-refinement policy was retired: it folds `feedType` and `orderingStyle`
        // into ONE menu, and five of its eight options share `feedType: "all"` — so the old rule made
        // three of eight peer options push and five replace, a distinction with no expression anywhere
        // in the interface. Reported 2026-08-22 as "the url changes, but when i go back it doesn't
        // actually go back". The escape cost (leaving home after three re-sorts now takes three
        // presses) is accepted, deliberately and with no compensating affordance — D2.

        if (nextFeedType === "all") {
            const commitAllSelection = (nextRange: HomeRange) => {
                setPendingSelection(null);
                setRememberedAllState((previous) => ({
                    ...previous,
                    orderingStyle: nextOrderingStyle,
                    // A resolved window is remembered exactly like a chosen one, so the URL, the cursor
                    // walk and the persisted `allTopRange` all keep today's contract. This is what
                    // keeps the slice free of a migration: the value written here is always one of the
                    // five concrete `HomeRange`s, never an `AUTO` sentinel the CHECK constraint on
                    // `application_users` would have had to be widened for.
                    topRange: homeOrderingSupportsRange(nextOrderingStyle) ? nextRange : previous.topRange,
                }));
                setPageState((previous) => normalizeHomePageState({
                    ...previous,
                    feedType: "all",
                    orderingStyle: nextOrderingStyle,
                    range: homeOrderingSupportsRange(nextOrderingStyle) ? nextRange : previous.range,
                }, isLoggedIn));
            };

            // The sorts with no time window, and the member who has pinned one by hand, commit
            // straight through on today's path.
            if (!homeOrderingSupportsRange(nextOrderingStyle) || rangePinnedByMemberRef.current) {
                commitAllSelection(rememberedAllState.topRange);
                return;
            }

            // Resolve BEFORE the commit, so the sort and its window land in ONE push and rule 33 needs
            // no replace exemption for this. The pill reflects the choice immediately via
            // `pendingSelection` while the probe runs, and the previous feed stays on screen — so the
            // member never sees a narrow-then-widen flicker, only a slightly later swap.
            setPendingSelection(value);
            const resolveRequestId = ++rangeResolveRequestIdRef.current;
            void resolveSmallestRangeWithContent(nextOrderingStyle, rememberedAllState.topRange)
                .then((resolvedRange) => {
                    if (resolveRequestId !== rangeResolveRequestIdRef.current) {
                        return;
                    }
                    commitAllSelection(resolvedRange);
                });
            return;
        }

        setPendingSelection(null);

        setPageState((previous) => {
            // A scope KEEPS the member's current sort (audience-scopes design §4, HS-3a): the sort on
            // screen when the previous feed had a sort axis (Everywhere or another scope), otherwise the
            // remembered all-feed sort (coming from Home, "No replies yet" or the board lane, whose
            // ordering is a placeholder). The window follows the same source when the sort takes one;
            // `normalizeHomePageState` then folds `controversial`/`arguments` to Active for the scope.
            // Home and the two lanes ignore both, as before.
            const previousHadSortAxis = homeFeedTypeSupportsSort(previous.feedType);
            return normalizeHomePageState({
                feedType: nextFeedType,
                orderingStyle: previousHadSortAxis ? previous.orderingStyle : rememberedAllState.orderingStyle,
                range: previousHadSortAxis ? previous.range : getEffectiveAllRange(rememberedAllState),
            }, isLoggedIn);
        });
    }, [isLoggedIn, rememberedAllState, setPageState]);
    /**
     * The sort pill (audience-scopes design §4, HS-3a). On Everywhere it IS the fused row: the same
     * path, so the range auto-narrow probe, the remembered state and the single push are the row's.
     * Inside a scope it writes the sort in place and keeps the feed, with no probe: the probe walks the
     * ALL-feed endpoints and would answer a question about a different corpus. It also writes the
     * remembered all-feed sort, so the persisted `allOrderingStyle` is the sort the member last chose
     * ANYWHERE and the server's param-less landing applies it inside the stored scope (HS-2a).
     */
    const handleOrderingStyleChange = React.useCallback((value: HomeOrderingStyle) => {
        if (feedType === "all") {
            handleSelectionChange(value);
            return;
        }
        hasUserChangedPreferencesRef.current = true;
        setPendingSelection(null);
        setRememberedAllState((previous) => ({ ...previous, orderingStyle: value }));
        setPageState((previous) => normalizeHomePageState({
            ...previous,
            orderingStyle: value,
            range: homeOrderingSupportsRange(value) ? rememberedAllState.topRange : previous.range,
        }, isLoggedIn));
    }, [feedType, handleSelectionChange, isLoggedIn, rememberedAllState.topRange, setPageState]);
    // Retained for HomeFeedList's empty-state CTAs (e.g. the "home" feed's "browse all" fallback).
    const handleFeedTypeChange = React.useCallback((value: HomeFeedType) => {
        hasUserChangedPreferencesRef.current = true;
        setPageState((previous) => normalizeHomePageState({
            ...previous,
            feedType: value,
            orderingStyle: value === "all" ? rememberedAllState.orderingStyle : previous.orderingStyle,
            range: value === "all" ? getEffectiveAllRange(rememberedAllState) : previous.range,
        }, isLoggedIn));
    }, [isLoggedIn, rememberedAllState, setPageState]);
    const handleRangeChange = React.useCallback((value: HomeRange) => {
        hasUserChangedPreferencesRef.current = true;
        // An explicit pick pins the window: auto-narrow must never overwrite a choice the member made
        // themselves. This also covers the empty-state "Clear filters" action, which routes here — a
        // member who just asked to widen must not be narrowed again by their next sort switch.
        rangePinnedByMemberRef.current = true;
        rangeResolveRequestIdRef.current += 1;
        setRememberedAllState((previous) => ({
            ...previous,
            topRange: value,
        }));
        // The time window PUSHES, like every other member action that changes the address (§13.1). It
        // used to replace on the grounds that it "narrows the feed you are already reading" — an
        // intent-based distinction the member cannot see, sitting one control away from the feed
        // selector that made the same reasoning indefensible.
        setPageState((previous) => normalizeHomePageState({ ...previous, range: value }, isLoggedIn));
    }, [isLoggedIn, setPageState]);

    const loadDiscoveryBootstrap = React.useCallback(() => {
        const requestId = ++discoveryRequestIdRef.current;
        const includeRecentlyViewed = isLoggedIn;

        setHistoryState(includeRecentlyViewed
            ? { items: [], loading: true, error: null }
            : { items: [], loading: false, error: null });
        setSeriesState({ items: [], loading: true, error: null });
        setGroupsState((prev) => ({ ...prev, loading: true, error: null }));
        setUsersState((prev) => ({ ...prev, loading: true, error: null }));

        void ClientFactory.getHomeDiscoveryBootstrap({ includeRecentlyViewed })
            .then((response) => {
                if (requestId !== discoveryRequestIdRef.current) {
                    return;
                }

                const data: HomeDiscoveryBootstrapResponse = response?.data ?? {
                    recommendedGroups: [],
                    recommendedSeries: [],
                    activePublishers: [],
                    recentlyViewedDiscussions: [],
                };

                setHistoryState({
                    items: includeRecentlyViewed ? (data.recentlyViewedDiscussions ?? []) : [],
                    loading: false,
                    error: null,
                });
                setSeriesState({
                    items: data.recommendedSeries ?? [],
                    loading: false,
                    error: null,
                });
                setGroupsState({
                    items: data.recommendedGroups ?? [],
                    loading: false,
                    error: null,
                });
                setUsersState({
                    items: data.activePublishers ?? [],
                    loading: false,
                    error: null,
                });
                setViewerGraphTier(data.viewerGraph?.tier ?? null);
            })
            .catch((error) => {
                if (requestId !== discoveryRequestIdRef.current || isInterruptedRequestError(error)) {
                    return;
                }

                const loadFailed = localizer("home.discovery.load_failed");
                setHistoryState({
                    items: [],
                    loading: false,
                    error: includeRecentlyViewed ? loadFailed : null,
                });
                setSeriesState({ items: [], loading: false, error: loadFailed });
                setGroupsState({ items: [], loading: false, error: loadFailed });
                setUsersState({ items: [], loading: false, error: loadFailed });
                // A failed bootstrap knows nothing about this member's graph, so it must claim nothing.
                // `null` is "no adaptation", never "empty graph": guessing EMPTY here would tell a
                // member with 200 follows that they follow nobody, on the strength of a network error.
                setViewerGraphTier(null);
                logger.error("[HomePage] failed to load discovery bootstrap", error);
            });
    }, [isLoggedIn, localizer]);

    /**
     * `HOME-FEED-27` (**D25**). Fires once, the first time the member is on the `groups` feed.
     *
     * <p>`groupsMemberOfUser` with no username resolves to the logged-in member server-side and caps
     * at 20 rows. The path is the controller's own `@GetMapping("/groups/user/member")` under
     * `AjaxPaginationController`, reached through the generated client — no hand-written wrapper and
     * no new endpoint (rule 6).
     *
     * <p>Guarded by a ref rather than by the state, so switching between feeds does not re-issue it,
     * and reset on failure so a transient error is retried the next time the member returns to this
     * feed. A failure leaves `items` empty, which renders as no module at all: this is a bonus
     * surface, and it must never turn a working feed into an error state.
     */
    React.useEffect(() => {
        if (!isLoggedIn || feedType !== "groups" || yourGroupsLoadedRef.current) {
            return;
        }

        yourGroupsLoadedRef.current = true;
        const requestId = ++yourGroupsRequestIdRef.current;
        setYourGroupsState({ items: [], loading: true, error: null });

        void ClientFactory.getAjaxPaginationRestControllerApi()
            .groupsMemberOfUser()
            .then((response) => {
                if (requestId !== yourGroupsRequestIdRef.current) {
                    return;
                }
                setYourGroupsState({ items: response?.data ?? [], loading: false, error: null });
            })
            .catch((error) => {
                if (requestId !== yourGroupsRequestIdRef.current || isInterruptedRequestError(error)) {
                    return;
                }
                yourGroupsLoadedRef.current = false;
                setYourGroupsState({ items: [], loading: false, error: null });
                logger.error("[HomePage] failed to load the viewer's groups", error);
            });
    }, [feedType, isLoggedIn]);

    React.useEffect(() => {
        loadDiscoveryBootstrap();
        return () => {
            // Invalidate any in-flight discovery request so a late response can't
            // overwrite state after unmount or after isLoggedIn changes.
            discoveryRequestIdRef.current++;
        };
    }, [loadDiscoveryBootstrap]);

    /*
     * The feed underneath learns about a follow or a join (2026-09-01, tier-1 finding 15).
     *
     * `loadDiscoveryBootstrap` runs once per document load, and the first-run overlay renders OVER this
     * page. So a member could follow six publishers and join two communities in the welcome carousel,
     * close it, and find the page beneath completely unchanged -- including the starter-feed banner
     * still reading "Follow people, groups and series, and their posts will show up here", which is a
     * false statement about a member who has just done exactly that. The rails were computed for a graph
     * that no longer exists.
     *
     * Two things this deliberately does NOT do:
     *
     * - It does not remount the feed. Changing the `key` on PaginatedListWrapperV2 is the only reset it
     *   has, and it throws away every loaded page and the scroll restoration with them. Re-running the
     *   bootstrap refreshes the rails, the tier and the banner while the feed pages stay exactly where
     *   the member left them -- and it is already the retry handler passed to two children, so it is a
     *   path meant to be re-entered.
     * - It does not refresh on every follow. The rails are worth recomputing while the member is still
     *   crossing out of an EMPTY or SPARSE graph, because that is when what this page can show them
     *   actually changes; an established member adding their sixtieth follow gains nothing from a
     *   refetch. `null` counts as worth refreshing -- the first load failed or has not landed, so there
     *   is no tier to reason from.
     *
     * The existing `discoveryRequestIdRef` race guard covers the refetch, so a late earlier response
     * cannot overwrite a newer one.
     */
    React.useEffect(() => {
        if (!isLoggedIn) {
            return;
        }

        const refreshWhileTheGraphStillMatters = () => {
            if (viewerGraphTier === "ESTABLISHED") {
                return;
            }
            loadDiscoveryBootstrap();
        };

        const unsubscribers = [
            onUserFollowAdded(refreshWhileTheGraphStillMatters),
            onGroupMemberAdded(refreshWhileTheGraphStillMatters),
        ];

        return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    }, [isLoggedIn, loadDiscoveryBootstrap, viewerGraphTier]);

    // HOME-FEED-29. The feed's sort and range pills live in page flow above card one, on a list that
    // appends ten more cards every time the sentinel is reached — so the main thing a member does on
    // this screen gets steadily more expensive the more they read, and the fixed header offers no
    // shortcut. The row's own cheaper option: a jump back to the controls, not a second pinned bar.
    //
    // It rides the existing floating-action host rather than adding a competing fixed element, because
    // a second fixed control would land on the Create FAB's corner (POLICY-CONTACT-27 is the same
    // collision seen from the policy pages). A contributed group ALWAYS wins the corner over the static
    // registry, so the group re-declares Create beside the jump; when the jump is not owed we publish
    // null and the static Create action resumes untouched.
    const controlRowRef = React.useRef<HTMLElement | null>(null);
    const [controlsOutOfView, setControlsOutOfView] = React.useState(false);

    React.useEffect(() => {
        const node = controlRowRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setControlsOutOfView(!entry.isIntersecting),
            {threshold: 0},
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const scrollToControlRow = React.useCallback(() => {
        controlRowRef.current?.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start",
        });
    }, []);

    useFloatingActionGroup(
        "home-feed-controls",
        controlsOutOfView
            ? {
                  scopeId: "home-feed-controls",
                  actions: [
                      {
                          id: "home-back-to-controls",
                          look: {
                              icon: ArrowUp,
                              labelKey: "home.controls.back-to-top",
                              variant: "neutral",
                              size: "md",
                          },
                          onClick: scrollToControlRow,
                          dataPlaywrightId: "floating-action-home-back-to-controls",
                      },
                      CREATE_DISCUSSION_ACTION,
                  ],
              }
            : null,
        [controlsOutOfView, scrollToControlRow],
    );

    return (
        <>
        <PageWithRailLayout
            dataPlaywrightId="home-page"
            // SV-138 (FRR `3a`): home opts into the phone edge shell, and this layout's `px-4` was the
            // SECOND 16px phone layer. Below `sm` it goes to 0 so feed rows run edge to edge on their own
            // `FEED_ROW_GRID_CLASS` `px-4`; the tab strip and composer already carry their own `px-4`, and
            // the SSR first card (painted straight into the mount div) now lands where this row does.
            className="max-sm:px-0"
            mainClassName="min-w-0"
            main={(
                <div data-playwright-id="home-main-feed">
                    <HomePageHeader
                        controlRowRef={controlRowRef}
                        localizer={localizer}
                        showPersonalOptions={isLoggedIn}
                        selection={displayedSelection}
                        range={range}
                        orderingStyle={displayedOrderingStyle}
                        rememberedAllOrderingStyle={rememberedAllState.orderingStyle}
                        onSelectionChange={handleSelectionChange}
                        onRangeChange={handleRangeChange}
                        onOrderingStyleChange={handleOrderingStyleChange}
                    />

                    {/* 1D honest-undo strip: when a ?lang= switch auto-added a language to the reading-set,
                        surface a one-tap, dismissible "Now showing X too — Undo · Manage" notice. Only
                        logged-in members can receive the server cookie that drives it. */}
                    {isLoggedIn ? <ReadingLanguageAddedStrip localizer={localizer} /> : null}

                    {/* The Google Play offer, on an Android phone browser only, members only (visitors
                        get the sidebar row). Persistent; a dismiss snoozes it: see androidAppPromoState. Design:
                        designs/01-now/2026-09-11-android-app-install-promotion-design.md §3.3. */}
                    {isLoggedIn ? <AndroidAppPromoStrip localizer={localizer} /> : null}

                    {/* The one-time "You earned a distinction" card (distinctions design §5, A10): members
                        only, since the celebration endpoint is owner-only. The slot asks the server and
                        loads the card's chunk only when there is something to celebrate. */}
                    {isLoggedIn ? <DistinctionCelebrationSlot localizer={localizer} /> : null}

                    <HomeFeedList
                        localizer={localizer}
                        feedType={feedType}
                        orderingStyle={orderingStyle}
                        range={range}
                        initialFeedItems={initialFeedItemsForCurrentFeed}
                        groupsState={groupsState}
                        seriesState={seriesState}
                        usersState={usersState}
                        historyState={historyState}
                        yourGroupsState={yourGroupsState}
                        viewerGraphTier={viewerGraphTier}
                        onFeedTypeChange={handleFeedTypeChange}
                        // `HOME-FEED-11`: the same handler the range pill uses, so "Clear filters" on
                        // the filtered-to-zero panel widens the window through one code path.
                        onRangeChange={handleRangeChange}
                        onRetry={loadDiscoveryBootstrap}
                    />
                </div>
            )}
            rail={(
                <HomeDiscoveryRail
                    historyState={historyState}
                    groupsState={groupsState}
                    seriesState={seriesState}
                    usersState={usersState}
                    feedType={feedType}
                    yourGroupsState={yourGroupsState}
                    localizer={localizer}
                    onRetry={loadDiscoveryBootstrap}
                />
            )}
        />
        </>
    );
}

export const mountHomePage = createReactMount({
    name: "mountHomePage",
    getProps: ({ dataset, parsed, localizer }) => ({
        localizer,
        initialFeedType: normalizeFeedType(dataset.feedType, MetaHandler.isUserLoggedIn()),
        initialOrderingStyle: normalizeOrderingStyle(dataset.ordering),
        initialRange: normalizeRange(dataset.range),
        initialAllOrderingStyle: normalizeOrderingStyle(dataset.allOrdering),
        initialAllRange: normalizeRange(dataset.allRange),
        // CWV L1: normalizeProps auto-parses the data-feed-first-page='[...]' JSON into an array.
        initialFeedItems: Array.isArray(parsed.feedFirstPage)
            ? (parsed.feedFirstPage as DiscussionWithoutContentDTO[])
            : undefined,
    }),
    render: (props) => <HomePage {...props} />,
});

export default HomePage;
